import axios from "axios";
import { resolveUserApiV1Base } from "../api/resolveBaseUrl";
import { isAuthRedirectInFlight, markAuthRedirectInFlight } from "./sessionRedirect";
import { syncHubSunsetFromPlatform, resetHubSunsetFeatureCache } from "./hubSunset";
function socialAppWindow() {
    return window;
}
const DEFAULT_DISABLED = {
    enabled: false,
    legacy_login: true,
};
let configPromise = null;
export function resetSsoConfigCache() {
    configPromise = null;
    const win = socialAppWindow();
    win.__SOCIAL_SSO_CONFIG__ = null;
    win.__SOCIAL_SSO_ENABLED__ = false;
    resetHubSunsetFeatureCache();
}
function applySsoConfig(cfg) {
    const win = socialAppWindow();
    win.__SOCIAL_SSO_CONFIG__ = cfg;
    win.__SOCIAL_SSO_ENABLED__ = !!(cfg && cfg.enabled);
    syncHubSunsetFromPlatform(cfg?.platform ?? null);
    return cfg;
}
export function getCachedSsoConfig() {
    if (typeof window === "undefined") {
        return null;
    }
    return socialAppWindow().__SOCIAL_SSO_CONFIG__ ?? null;
}
function parseSsoConfigBody(raw) {
    if (!raw || typeof raw !== "object") {
        return { ...DEFAULT_DISABLED };
    }
    return {
        enabled: !!raw.enabled,
        issuer: raw.issuer,
        tenant: raw.tenant,
        login_url: raw.login_url,
        authorize_url: (raw.authorize_url ?? raw.social_authorize_url),
        social_authorize_url: (raw.social_authorize_url ?? raw.authorize_url),
        social_authorize_silent_url: raw.social_authorize_silent_url,
        social_login_url: raw.social_login_url,
        social_popup_authorize_url: raw.social_popup_authorize_url,
        session_hint_url: raw.session_hint_url,
        logout_url: raw.logout_url,
        redirect_uri: raw.redirect_uri,
        token_audience: raw.token_audience,
        session_cookie: raw.session_cookie,
        legacy_login: raw.legacy_login !== false,
        post_login_path: raw.post_login_path,
        popup_width: Number(raw.popup_width) || 420,
        popup_height: Number(raw.popup_height) || 560,
        platform: raw.platform,
    };
}
export async function loadSsoConfig(force = false) {
    if (typeof window === "undefined") {
        return null;
    }
    if (isAuthRedirectInFlight()) {
        return getCachedSsoConfig() ?? { ...DEFAULT_DISABLED };
    }
    const buildTimeOff = String(process.env.VUE_APP_SSO_ENABLED || "").toLowerCase() === "false";
    if (buildTimeOff) {
        return applySsoConfig({ ...DEFAULT_DISABLED });
    }
    const win = socialAppWindow();
    if (!force && win.__SOCIAL_SSO_CONFIG__) {
        return win.__SOCIAL_SSO_CONFIG__;
    }
    if (!force && configPromise) {
        return configPromise;
    }
    configPromise = axios
        .get(`${resolveUserApiV1Base()}/auth/sso/config`, {
        validateStatus: (status) => status < 500,
        headers: {
            Accept: "application/json",
            ...(typeof window !== "undefined" && window.location?.hostname
                ? { "X-Social-Frontend-Host": window.location.hostname }
                : {}),
        },
    })
        .then((res) => {
        if (res.status === 404 || res.status === 429 || res.status >= 400) {
            return applySsoConfig({ ...DEFAULT_DISABLED });
        }
        const body = res.data;
        return applySsoConfig(parseSsoConfigBody(body?.data));
    })
        .catch(() => applySsoConfig({ ...DEFAULT_DISABLED }))
        .finally(() => {
        configPromise = null;
    });
    return configPromise;
}
/** Cookie JWT hợp lệ — không bootstrap Sanctum. */
export async function fetchAuthMe() {
    try {
        const res = await axios.get(`${resolveUserApiV1Base()}/auth/me`, {
            withCredentials: true,
            validateStatus: (status) => status < 500,
            headers: {
                Accept: "application/json",
                ...(typeof window !== "undefined" && window.location?.hostname
                    ? { "X-Social-Frontend-Host": window.location.hostname }
                    : {}),
            },
        });
        return res.status >= 200 && res.status < 300;
    }
    catch {
        return false;
    }
}
function normalizeIdpSessionHint(raw) {
    if (!raw || typeof raw !== "object") {
        return { status: "unauthenticated" };
    }
    const body = raw;
    const status = String(body.status ?? "unauthenticated");
    const payload = body.data;
    if (!payload || Array.isArray(payload) || typeof payload !== "object") {
        return {
            status: status === "authenticated" ? "unauthenticated" : status,
        };
    }
    const d = payload;
    return {
        status,
        data: {
            user_id: typeof d.user_id === "number" ? d.user_id : undefined,
            name: String(d.name ?? d.display_name ?? "").trim() || undefined,
            avatar_url: String(d.avatar_url ?? d.avatar ?? "").trim() || undefined,
        },
    };
}
async function requestIdpStorageAccess() {
    if (typeof document === "undefined") {
        return;
    }
    const doc = document;
    if (typeof doc.requestStorageAccess !== "function") {
        return;
    }
    try {
        if (typeof doc.hasStorageAccess === "function" && (await doc.hasStorageAccess())) {
            return;
        }
        await doc.requestStorageAccess();
    }
    catch {
        /* user denied or browser policy */
    }
}
/** IdP session hint — user đã login hub/agency trên sitenora.com? */
export async function fetchIdpSessionHint(cfg) {
    const c = cfg ?? getCachedSsoConfig();
    const url = c?.session_hint_url;
    if (!url || typeof fetch !== "function") {
        return null;
    }
    try {
        await requestIdpStorageAccess();
        const res = await fetch(url, {
            method: "GET",
            credentials: "include",
            mode: "cors",
            headers: { Accept: "application/json" },
        });
        if (!res.ok) {
            return { status: "unauthenticated" };
        }
        return normalizeIdpSessionHint(await res.json());
    }
    catch {
        return { status: "unauthenticated" };
    }
}
/** Alias tương thích Agency Portal. */
export async function fetchIdpSessionActive(cfg) {
    const hint = await fetchIdpSessionHint(cfg);
    return hint?.status === "authenticated";
}
export function mustUseSsoOnly(cfg) {
    const c = cfg ?? getCachedSsoConfig();
    return !!(c && c.enabled && c.legacy_login === false);
}
export function isSsoEnabled() {
    const win = socialAppWindow();
    if (win.__SOCIAL_SSO_ENABLED__ != null) {
        return !!win.__SOCIAL_SSO_ENABLED__;
    }
    const cfg = getCachedSsoConfig();
    return !!(cfg && cfg.enabled);
}
export function ssoLoginUrl(cfg) {
    const c = cfg || getCachedSsoConfig();
    if (c?.login_url) {
        return c.login_url;
    }
    const proto = typeof window !== "undefined" && window.location.protocol === "https:"
        ? "https:"
        : "http:";
    const host = typeof window !== "undefined" ? window.location.host : "";
    return `${proto}//${host}/sso/login`;
}
export function resolveSsoAuthorizeUrl(cfg, idpAuthenticated = false) {
    const c = cfg || getCachedSsoConfig();
    if (idpAuthenticated && c?.social_authorize_silent_url) {
        return c.social_authorize_silent_url;
    }
    if (c?.social_authorize_url) {
        return c.social_authorize_url;
    }
    if (c?.authorize_url) {
        return c.authorize_url;
    }
    return ssoLoginUrl(c);
}
export function redirectToSsoAuthorize(cfg, idpAuthenticated = false) {
    if (typeof window === "undefined") {
        return;
    }
    markAuthRedirectInFlight();
    window.location.replace(resolveSsoAuthorizeUrl(cfg, idpAuthenticated));
}
export function redirectToSsoLogin(cfg) {
    redirectToSsoAuthorize(cfg, false);
}
export function isLegacyRedirectOnly(cfg) {
    const c = cfg ?? getCachedSsoConfig();
    return !!(c?.platform && c.platform.legacy_redirect_only);
}
export function ssoErrorFromRoute() {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        const params = new URLSearchParams(window.location.search);
        return params.get("sso_error");
    }
    catch {
        return null;
    }
}
export function idpLogoutUrl(cfg) {
    const c = cfg ?? getCachedSsoConfig();
    return c?.logout_url ?? null;
}
const SSO_POPUP_WINDOW_NAME = "sotax_sso";
export function ssoPopupFeatures(cfg) {
    const c = cfg ?? getCachedSsoConfig();
    const w = c?.popup_width ?? 420;
    const h = c?.popup_height ?? 560;
    let left = 0;
    let top = 0;
    if (typeof window !== "undefined") {
        const screenLeft = window.screenLeft ?? window.screenX ?? 0;
        const screenTop = window.screenTop ?? window.screenY ?? 0;
        const viewportW = window.outerWidth || window.innerWidth || w;
        const viewportH = window.outerHeight || window.innerHeight || h;
        left = Math.round(screenLeft + Math.max(0, (viewportW - w) / 2));
        top = Math.round(screenTop + Math.max(0, (viewportH - h) / 2));
    }
    return `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`;
}
/** URL mở popup — Case B silent khi IdP đã authenticated, Case A login/popup authorize. */
export function resolveSsoPopupUrl(cfg, idpAuthenticated = false) {
    const c = cfg ?? getCachedSsoConfig();
    if (idpAuthenticated && c?.social_authorize_silent_url) {
        return c.social_authorize_silent_url;
    }
    if (c?.social_login_url) {
        return c.social_login_url;
    }
    if (c?.social_popup_authorize_url) {
        return c.social_popup_authorize_url;
    }
    return resolveSsoAuthorizeUrl(c, false);
}
export function openSsoPopup(cfg, idpAuthenticated = false) {
    if (typeof window === "undefined") {
        return null;
    }
    const url = resolveSsoPopupUrl(cfg, idpAuthenticated);
    markAuthRedirectInFlight();
    return window.open(url, SSO_POPUP_WINDOW_NAME, ssoPopupFeatures(cfg));
}
/** Theo dõi popup đóng mà chưa nhận postMessage success/error. */
export function watchSsoPopupUntilClosed(popup, onClosed) {
    const noop = () => undefined;
    if (!popup) {
        return { markSettled: noop, stop: noop };
    }
    let settled = false;
    let timer = null;
    const stopTimer = () => {
        if (timer !== null) {
            clearInterval(timer);
            timer = null;
        }
    };
    const markSettled = () => {
        settled = true;
        stopTimer();
    };
    const stop = () => {
        markSettled();
    };
    const handleClosed = () => {
        if (settled) {
            return;
        }
        settled = true;
        stopTimer();
        onClosed();
    };
    timer = setInterval(() => {
        if (settled) {
            stopTimer();
            return;
        }
        try {
            if (popup.closed) {
                handleClosed();
            }
        }
        catch {
            handleClosed();
        }
    }, 400);
    return { markSettled, stop };
}
export function installSsoPopupMessageListener(handlers) {
    if (typeof window === "undefined") {
        return () => undefined;
    }
    const expectedOrigin = window.location.origin;
    const onMessage = (event) => {
        if (event.origin !== expectedOrigin) {
            return;
        }
        const data = event.data;
        if (!data || typeof data !== "object" || !data.type) {
            return;
        }
        if (data.type === "sotax_sso_success") {
            handlers.onSuccess(data.redirect || getCachedSsoConfig()?.post_login_path || "/new-feeds");
            return;
        }
        if (data.type === "sotax_sso_error") {
            handlers.onError(data.error || "unknown");
        }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
}
